import { useTranslation } from "react-i18next";

import { PIN_STATUSES, PIN_TYPES, QUESTION_RESULT_STATUSES, WARRANTY_TYPES } from "models";

import type { DropdownItemProps } from "components/Dropdown";

export enum REG_VIN_FILTER {
  Reg = 1,
  Vin
}

export enum ORDER_BY {
  AppointmentDateAscending = 1,
  AppointmentDateDescending,
  PinUpdatedOnAscending,
  PinUpdatedOnDescending,
  ClaimAscending,
  ClaimDescending
}

export enum ORDER_BY_LOCATION {
  LocationAscending = 1,
  LocationDescending
}

export enum WARRANTY_TIME_PERIODS {
  CustomTimePeriod = 0,
  LastMonth = 1,
  LastThreeMonths = 3,
  LastSixMonths = 6,
  LastNineMonths = 9,
  LastYear = 12,
  LastTwoYears = 24
}

export const useRegVinNumberOptions = (): DropdownItemProps[] => {
  const { t } = useTranslation();

  return [
    { key: REG_VIN_FILTER.Reg, value: REG_VIN_FILTER.Reg, text: t("v8_reg_number", "Reg #") },
    { key: REG_VIN_FILTER.Vin, value: REG_VIN_FILTER.Vin, text: t("v8_vin_number", "Vin #") }
  ];
};

export enum MECHANIC_FIXED {
  Yes = 1,
  No = 2
}
export const useGetMechanicFixedOptions = (): DropdownItemProps[] => {
  const { t } = useTranslation();

  return [
    { key: "mechanic-fixed-1", value: MECHANIC_FIXED.Yes, text: t("v8_yes", "Yes") },
    { key: "mechanic-fixed-2", value: MECHANIC_FIXED.No, text: t("v8_no", "No") }
  ];
};

export const useGetTimePeriodOptions = (): DropdownItemProps<number | string>[] => {
  const { t } = useTranslation();

  return [
    {
      key: `time-period-${WARRANTY_TIME_PERIODS.LastMonth}`,
      value: WARRANTY_TIME_PERIODS.LastMonth,
      text: t("v8_pinned_in_the_last_month", "Pinned in the last month")
    },
    {
      key: `time-period-${WARRANTY_TIME_PERIODS.LastThreeMonths}`,
      value: WARRANTY_TIME_PERIODS.LastThreeMonths,
      text: t("v8_pinned_in_the_last_3_months", "Pinned in the last 3 months")
    },
    {
      key: `time-period-${WARRANTY_TIME_PERIODS.LastSixMonths}`,
      value: WARRANTY_TIME_PERIODS.LastSixMonths,
      text: t("v8_pinned_in_the_last_6_months", "Pinned in the last 6 months")
    },
    {
      key: `time-period-${WARRANTY_TIME_PERIODS.LastNineMonths}`,
      value: WARRANTY_TIME_PERIODS.LastNineMonths,
      text: t("v8_pinned_in_the_last_9_months", "Pinned in the last 9 months")
    },
    {
      key: `time-period-${WARRANTY_TIME_PERIODS.LastYear}`,
      value: WARRANTY_TIME_PERIODS.LastYear,
      text: t("v8_pinned_in_the_last_12_months", "Pinned in the last 12 months")
    },
    {
      key: `time-period-${WARRANTY_TIME_PERIODS.LastTwoYears}`,
      value: WARRANTY_TIME_PERIODS.LastTwoYears,
      text: t("v8_pinned_in_the_last_24_months", "Pinned in the last 24 months")
    },
    {
      key: `time-period-${WARRANTY_TIME_PERIODS.CustomTimePeriod}`,
      value: WARRANTY_TIME_PERIODS.CustomTimePeriod,
      text: t("v8_custom_pin_dates", "Custom pin dates")
    }
  ];
};

export const useGetAppointmentPeriodOptions = (): DropdownItemProps<number | string>[] => {
  const { t } = useTranslation();

  return [
    {
      key: `appointment-period-${WARRANTY_TIME_PERIODS.LastMonth}`,
      value: WARRANTY_TIME_PERIODS.LastMonth,
      text: t("v8_appointments_in_last_month", "Appointments in last month")
    },
    {
      key: `appointment-period-${WARRANTY_TIME_PERIODS.LastThreeMonths}`,
      value: WARRANTY_TIME_PERIODS.LastThreeMonths,
      text: t("v8_appointments_in_last_3_months", "Appointments in last 3 months")
    },
    {
      key: `appointment-period-${WARRANTY_TIME_PERIODS.LastSixMonths}`,
      value: WARRANTY_TIME_PERIODS.LastSixMonths,
      text: t("v8_appointments_in_last_6_months", "Appointments in last 6 months")
    },
    {
      key: `appointment-period-${WARRANTY_TIME_PERIODS.LastNineMonths}`,
      value: WARRANTY_TIME_PERIODS.LastNineMonths,
      text: t("v8_appointments_in_last_9_months", "Appointments in last 9 months")
    },
    {
      key: `appointment-period-${WARRANTY_TIME_PERIODS.LastYear}`,
      value: WARRANTY_TIME_PERIODS.LastYear,
      text: t("v8_appointments_in_last_12_months", "Appointments in last 12 months")
    },
    {
      key: `appointment-period-${WARRANTY_TIME_PERIODS.LastTwoYears}`,
      value: WARRANTY_TIME_PERIODS.LastTwoYears,
      text: t("v8_appointments_in_last_24_months", "Appointments in last 24 months")
    },
    {
      key: `appointment-period-${WARRANTY_TIME_PERIODS.CustomTimePeriod}`,
      value: WARRANTY_TIME_PERIODS.CustomTimePeriod,
      text: t("v8_custom_appointment_dates", "Custom appointment dates")
    }
  ];
};

export const useLocationOrderingOptions = (): DropdownItemProps<number | string>[] => {
  const { t } = useTranslation();

  return [
    {
      key: `location-ordering-all`,
      value: "all",
      text: t("v8_no_sorting", "No sorting")
    },
    {
      key: `location-ordering-${ORDER_BY_LOCATION.LocationAscending}`,
      value: ORDER_BY_LOCATION.LocationAscending,
      text: t("v8_location", "Location"),
      icon: "arrow down a z"
    },
    {
      key: `location-ordering-${ORDER_BY_LOCATION.LocationDescending}`,
      value: ORDER_BY_LOCATION.LocationDescending,
      text: t("v8_location", "Location"),
      icon: "arrow up a z"
    }
  ];
};

export const useAppointmentOrderingOptions = (): DropdownItemProps[] => {
  const { t } = useTranslation();

  return [
    {
      key: `appointment-ordering-${ORDER_BY.AppointmentDateAscending}`,
      value: ORDER_BY.AppointmentDateAscending,
      text: t("v8_appointment_date", "Appointment date"),
      icon: "arrow down a z"
    },
    {
      key: `appointment-ordering-${ORDER_BY.AppointmentDateDescending}`,
      value: ORDER_BY.AppointmentDateDescending,
      text: t("v8_appointment_date", "Appointment date"),
      icon: "arrow up a z"
    },
    {
      key: `appointment-ordering-${ORDER_BY.PinUpdatedOnAscending}`,
      value: ORDER_BY.PinUpdatedOnAscending,
      text: t("v8_last_updated", "Last updated"),
      icon: "arrow down a z"
    },
    {
      key: `appointment-ordering-${ORDER_BY.PinUpdatedOnDescending}`,
      value: ORDER_BY.PinUpdatedOnDescending,
      text: t("v8_last_updated", "Last updated"),
      icon: "arrow up a z"
    }
  ];
};

export const useGetPinStatusOptions = (): DropdownItemProps[] => {
  const { t } = useTranslation();

  return [
    { key: `pin-status-${PIN_STATUSES.NotSet}`, value: PIN_STATUSES.NotSet, text: t("v8_not_set", "Not set") },
    { key: `pin-status-${PIN_STATUSES.Support}`, value: PIN_STATUSES.Support, text: t("v8_support", "Support") },
    { key: `pin-status-${PIN_STATUSES.ApprovalPending}`, value: PIN_STATUSES.ApprovalPending, text: t("v8_approval_pending", "Approval pending") },
    { key: `pin-status-${PIN_STATUSES.MoreInfo}`, value: PIN_STATUSES.MoreInfo, text: t("v8_more_information", "More Info") },
    { key: `pin-status-${PIN_STATUSES.Approved}`, value: PIN_STATUSES.Approved, text: t("v8_approved", "Approved") },
    { key: `pin-status-${PIN_STATUSES.Declined}`, value: PIN_STATUSES.Declined, text: t("v8_declined", "Declined") },
    { key: `pin-status-${PIN_STATUSES.ToClaim}`, value: PIN_STATUSES.ToClaim, text: t("v8_to_claim", "To claim") },
    { key: `pin-status-${PIN_STATUSES.Claimed}`, value: PIN_STATUSES.Claimed, text: t("v8_claimed", "Claimed") },
    { key: `pin-status-${PIN_STATUSES.Billed}`, value: PIN_STATUSES.Billed, text: t("v8_billed", "Billed") },
    { key: `pin-status-${PIN_STATUSES.Request}`, value: PIN_STATUSES.Request, text: t("v8_request", "Request") },
    { key: `pin-status-${PIN_STATUSES.TechnicalInfo}`, value: PIN_STATUSES.TechnicalInfo, text: t("v8_technical_info", "Technical info") },
    { key: `pin-status-${PIN_STATUSES.Deleted}`, value: PIN_STATUSES.Deleted, text: t("v8_deleted", "Deleted") }
  ];
};

export const useGetPinWarrantyTypeOptions = (): DropdownItemProps<number | string>[] => {
  const { t } = useTranslation();

  return [
    { key: `pin-warranty-all`, value: "all", text: t("v8_all", "All") },
    { key: `pin-warranty-${WARRANTY_TYPES.NotSet}`, value: WARRANTY_TYPES.NotSet, text: t("v8_not_set", "Not Set") },
    { key: `pin-warranty-${WARRANTY_TYPES.Factory}`, value: WARRANTY_TYPES.Factory, text: t("v8_factory", "Factory") },
    { key: `pin-warranty-${WARRANTY_TYPES.Leniency}`, value: WARRANTY_TYPES.Leniency, text: t("v8_leniency", "Leniency") },
    { key: `pin-warranty-${WARRANTY_TYPES.BodyPaint}`, value: WARRANTY_TYPES.BodyPaint, text: t("v8_body_paint", "Body Paint") },
    { key: `pin-warranty-${WARRANTY_TYPES.OnRepair}`, value: WARRANTY_TYPES.OnRepair, text: t("v8_on_repair", "On Repair") },
    { key: `pin-warranty-${WARRANTY_TYPES.Occasion}`, value: WARRANTY_TYPES.Occasion, text: t("v8_occasion", "Occasion") },
    { key: `pin-warranty-${WARRANTY_TYPES.Service}`, value: WARRANTY_TYPES.Service, text: t("v8_service", "Service") }
  ];
};

export const useGetPinTypeOptions = (): DropdownItemProps<number | string>[] => {
  const { t } = useTranslation();

  return [
    { key: `pin-type-all`, value: "all", text: t("v8_all", "All") },
    { key: `pin-type-${PIN_TYPES.Remarks}`, value: PIN_TYPES.Remarks, text: t("v8_remarks", "Remarks") },
    { key: `pin-type-${PIN_TYPES.Warranty}`, value: PIN_TYPES.Warranty, text: t("v8_warranty", "Warranty") },
    { key: `pin-type-${PIN_TYPES.Recall}`, value: PIN_TYPES.Recall, text: t("v8_recall", "Recall") }
  ];
};

export const useGetQuestionStatusOptions = (): DropdownItemProps<string | number>[] => {
  const { t } = useTranslation();

  return [
    { key: "all", value: "all", text: t("v8_all", "All") },
    {
      key: QUESTION_RESULT_STATUSES.OK,
      value: QUESTION_RESULT_STATUSES.OK,
      text: t("v8_ok", "Ok"),
      icon: "file",
      iconColor: "#21BA45"
    },
    {
      key: QUESTION_RESULT_STATUSES.Advice,
      value: QUESTION_RESULT_STATUSES.Advice,
      text: t("v8_advice", "Advice"),
      icon: "triangle exclamation",
      iconColor: "#FFBA39"
    },
    {
      key: QUESTION_RESULT_STATUSES.Necessary,
      value: QUESTION_RESULT_STATUSES.Necessary,
      text: t("v8_necessary", "Necessary"),
      icon: "circle exclamation",
      iconColor: "#FF0202"
    }
  ];
};
